import {css, StyleSheet} from "aphrodite";

import {IntegerField} from "@khanacademy/event-schemas";
import {DetailCell} from "@khanacademy/wonder-blocks-cell";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {color, semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {mediaQueries} from "@khan/shared-styles";
import {redirect, staticUrl, useLocation} from "@khan/urls";

type Props = {
    index: number;
    headerLeft: string | null | undefined;
    headerRight: string | null | undefined;
    title: string | null | undefined;
    description: string | null | undefined;
    url: string | null | undefined;
    icon: string | null | undefined;
    iconUrl: string | null | undefined;
    contentKind: string | null | undefined;
    contentId: string | null | undefined;
    currentPage: number | null | undefined;
};

const Result = ({
    index,
    contentKind,
    headerLeft,
    headerRight,
    title,
    description,
    url,
    icon,
    iconUrl,
    contentId,
    currentPage,
}: Props) => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const query = useLocation().queryParams.page_search_query;
    const iconComponent = () => {
        if (icon) {
            return (
                <PhosphorIcon
                    icon={icon}
                    size="medium"
                    color={color.offBlack64}
                    style={styles.contentIcon}
                />
            );
        }

        if (headerRight === "Khanmigo") {
            return (
                <div className={css(styles.khanmigoIconContainer)}>
                    <img
                        className={css(styles.khanmigoIcon)}
                        src={staticUrl(iconUrl || "")}
                        alt=""
                    />
                </div>
            );
        }

        return (
            <img
                className={css(styles.topicIcon)}
                src={staticUrl(iconUrl || "")}
                alt=""
            />
        );
    };

    const handleResultClick = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        await publishAnalyticsEvent({
            eventType: "SearchResultClickedTI",
            eventSchemaVersion: 1,
            searchTerm: query,
            currentPageResultIndex: new IntegerField(index),
            currentPageNumber: new IntegerField(currentPage ?? 1),
            contentId: contentId ?? "",
            contentKind: contentKind ?? "",
        });

        redirect(url || "");
    };

    return (
        <View key={index} style={styles.cellContainer}>
            <DetailCell
                // This actually shows at the top of the cell.
                subtitle1={
                    <View style={styles.subtitleContainer}>
                        <View style={styles.cellSubtitle}>{headerLeft}</View>
                        <View style={styles.cellSubtitleRight}>
                            {headerRight}
                        </View>
                    </View>
                }
                title={<View style={styles.cellTitle}>{title}</View>}
                subtitle2={description || ""}
                onClick={handleResultClick}
                href={url || ""}
                leftAccessory={
                    <View style={styles.contentIconContainer}>
                        {iconComponent()}
                    </View>
                }
                style={styles.cell}
                horizontalRule="none"
            />
        </View>
    );
};

const styles = StyleSheet.create({
    cellContainer: {
        paddingTop: spacing.xxxSmall_4,
        paddingBottom: spacing.xxxSmall_4,
        [mediaQueries.smOrLarger]: {
            paddingLeft: spacing.xxSmall_6,
        },
    },
    cell: {
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: semanticColor.core.border.neutral.subtle,
    },
    cellTitle: {
        color: color.blue,
        fontSize: 16,
        fontWeight: 700,
        paddingTop: spacing.xxxSmall_4,
        paddingBottom: spacing.xxxSmall_4,
    },
    cellSubtitleRight: {
        alignItems: "flex-end",
        color: color.offBlack64,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        fontSize: 12,
    },
    cellSubtitle: {
        alignItems: "flex-start",
        color: color.offBlack64,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        fontSize: 12,
    },
    contentIconContainer: {
        width: 48,
        height: spacing.xxLarge_48,
        marginRight: spacing.small_12,
        alignItems: "center",
        border: `1px solid ${color.offBlack32}`,
        borderRadius: 100,
    },
    contentIcon: {
        marginTop: 10,
    },
    topicIcon: {
        borderRadius: 100,
        width: spacing.xxLarge_48,
    },
    khanmigoIconContainer: {
        width: spacing.xxLarge_48,
        height: "100%",
        borderRadius: 100,
        background: color.eggplant,
        display: "flex",
        justifyContent: "center",
    },
    khanmigoIcon: {
        width: spacing.large_24,
    },
    subtitleContainer: {
        flexDirection: "row",
        alignItems: "baseline",
        justifyContent: "space-between",
        // Mobile view: vertical stacking
        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
        },
    },
});

export default Result;
